import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { canRedo, canUndo, formatTimecode } from '@easyedit/timeline'
import { api } from '../../lib/api/client'
import type { Project } from '../../lib/api/types'
import { useEditorStore, useDuration } from '../../stores/editorStore'
import { navigate } from '../../app/App'
import { ExportDialog } from './ExportDialog'
import { isTextEntry } from '../../lib/dom/focus'

export function Topbar({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const history = useEditorStore((s) => s.history)
  const undo = useEditorStore((s) => s.undo)
  const redo = useEditorStore((s) => s.redo)
  const saving = useEditorStore((s) => s.saving)
  const pending = useEditorStore((s) => s.pending)
  const sequence = useEditorStore((s) => s.sequence)
  const duration = useDuration()
  const [showExport, setShowExport] = useState(false)

  const saveVersion = useMutation({
    mutationFn: () => api.post(`/projects/${project.id}/versions`, { label: '' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['project', project.id] }),
  })

  // Ctrl+Z / Ctrl+Shift+Z são musculares num editor. Ignorar quando o foco está num
  // campo de texto evita sequestrar o undo do próprio input.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTextEntry(event.target)) return
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        void (event.shiftKey ? redo() : undo())
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault()
        void redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  const fps = sequence?.frameRate ?? { num: 30, den: 1 }

  return (
    <header className="h-12 shrink-0 border-b border-ink-750 bg-ink-850 flex items-center gap-3 px-3">
      <button className="btn-ghost shrink-0" onClick={() => navigate('/')} title="Voltar aos projetos">
        ←
      </button>

      <div className="min-w-0 flex items-baseline gap-2">
        <span className="text-sm font-semibold truncate">{project.name}</span>
        <span className="font-mono text-2xs text-ink-500 shrink-0">
          {sequence?.width}×{sequence?.height} · {Math.round(fps.num / fps.den)}fps
        </span>
      </div>

      <div className="flex items-center gap-0.5 ml-2">
        <button
          className="btn-ghost"
          onClick={() => void undo()}
          disabled={!canUndo(history)}
          title={canUndo(history) ? `Desfazer: ${history.undo.at(-1)?.label}` : 'Nada para desfazer'}
        >
          ↶
        </button>
        <button
          className="btn-ghost"
          onClick={() => void redo()}
          disabled={!canRedo(history)}
          title="Refazer"
        >
          ↷
        </button>
      </div>

      <SaveIndicator saving={saving || pending > 0} />

      <div className="flex-1" />

      <span className="font-mono text-2xs text-ink-400 tnum hidden sm:block">
        {formatTimecode(duration, fps)}
      </span>

      <button
        className="btn-outline"
        onClick={() => saveVersion.mutate()}
        disabled={saveVersion.isPending}
        title="Salvar uma versão restaurável do projeto"
      >
        {saveVersion.isSuccess ? 'Versão salva' : 'Salvar versão'}
      </button>
      <button className="btn-primary" onClick={() => setShowExport(true)}>Exportar</button>

      {showExport && <ExportDialog project={project} onClose={() => setShowExport(false)} />}
    </header>
  )
}

function SaveIndicator({ saving }: { saving: boolean }) {
  const [showSaved, setShowSaved] = useState(false)

  // "Salvo" pisca por um instante depois de terminar; um rótulo permanente vira ruído.
  useEffect(() => {
    if (saving) return
    setShowSaved(true)
    const timer = window.setTimeout(() => setShowSaved(false), 1600)
    return () => window.clearTimeout(timer)
  }, [saving])

  if (saving) {
    return (
      <span className="flex items-center gap-1.5 text-2xs text-ink-400">
        <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        Salvando
      </span>
    )
  }
  return (
    <span className={`text-2xs text-ink-500 transition-opacity ${showSaved ? 'opacity-100' : 'opacity-0'}`}>
      Salvo
    </span>
  )
}
